import { env } from "@/env";
import { getLocale } from "@/paraglide/runtime";
import {
	isNotProduction,
	useViteEnvironment,
} from "@bidbattles/bloks/hooks/dev";
import { Result } from "@bidbattles/bloks/lib/result";
import { useSuspenseQuery } from "@tanstack/react-query";
import { merge } from "es-toolkit";
import { current } from "immer";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	MOLLIE_COMPONENTS,
	type MollieComponent,
	type MollieComponentOptions,
	type MollieComponentType,
	type MollieInstance,
} from "./types";

const DEFAULT_SCRIPT_URL = "https://js.mollie.com/v1/mollie.js";

export interface UseMollieResult {
	mollie: MollieInstance;
	components: Record<MollieComponentType, MollieComponent>;
	createToken(): Promise<Result<string>>;
}
type InitializeResult = Pick<UseMollieResult, "mollie" | "components">;

const componentOptions: Partial<
	Record<MollieComponentType | "common", MollieComponentOptions>
> = {
	common: {
		styles: {
			base: {
				color: "#101828",
				fontSize: "1rem",
				"::placeholder": {
					color: "#d0d5dd",
				},
			},
		},
	},
};

export function useMollie(): UseMollieResult {
	const viteEnv = useViteEnvironment();
	const query = useSuspenseQuery({
		queryKey: ["mollie", viteEnv],
		queryFn: ({ signal }) => {
			return Result.unwrapAsync(
				new Promise<Result<InitializeResult>>((resolve) => {
					const initializeMollie = (): Result<InitializeResult> => {
						const profileId = env.VITE_MOLLIE_PROFILE_ID;
						if (!profileId) {
							return Result.error(new Error("mollie not configured"));
						}

						const Mollie = window.Mollie;
						if (!Mollie) {
							return Result.error(new Error("mollie constructor missing"));
						}

						const result = Result.try(() =>
							Mollie(profileId, {
								locale: getLocale(),
								testmode: isNotProduction(viteEnv),
							}),
						);
						if (!result.ok) {
							return result;
						}
						const mollie = result.data;

						const componentList = MOLLIE_COMPONENTS.map((name) => {
							const defaultOptions = merge(
								merge({}, componentOptions.common ?? {}),
								componentOptions[name] ?? {},
							);
							return [
								name,
								mollie.createComponent(name, defaultOptions),
							] as const;
						});
						const components = Object.fromEntries(componentList) as Record<
							MollieComponentType,
							MollieComponent
						>;

						return Result.ok({ mollie, components });
					};

					const handleLoad = () => {
						const result = initializeMollie();
						resolve(result);
					};
					const handleError = (event: ErrorEvent) => {
						resolve(Result.error(event.error));
					};

					const result = initializeMollie();
					if (result.ok) {
						resolve(result);
						return;
					}

					let script = document.querySelector<HTMLScriptElement>(
						`script[src="${DEFAULT_SCRIPT_URL}"]`,
					);
					if (!script) {
						script = document.createElement("script");
						script.src = DEFAULT_SCRIPT_URL;
						script.async = true;
						document.head.appendChild(script);
					}

					script.addEventListener("load", handleLoad);
					script.addEventListener("error", handleError);
					signal.addEventListener("abort", () => {
						script.removeEventListener("load", handleLoad);
						script.removeEventListener("error", handleError);
					});
				}),
			);
		},
		// The Mollie instance should be cached forever
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
	});

	const mollie = query.data.mollie;
	const createToken = useCallback(async (): Promise<Result<string>> => {
		const result = await Result.tryAsync(() => mollie.createToken());
		if (!result.ok) {
			return result;
		}
		const tokenResult = result.data;

		if (tokenResult.error || !tokenResult.token) {
			const message = tokenResult.error
				? tokenResult.error.message || tokenResult.error.detail
				: "internal provider error";
			return Result.error(new Error(message));
		}

		return Result.ok(tokenResult.token);
	}, [mollie]);

	return {
		...query.data,
		createToken,
	};
}

export function useMollieComponent<E extends HTMLElement = HTMLDivElement>(
	type: MollieComponentType,
	ref: React.RefObject<E | null>,
): MollieComponent {
	const { components } = useMollie();
	const component = components[type];

	useEffect(() => {
		if (ref.current) {
			component.mount(ref.current);
			return () => {
				component.unmount();
			};
		}
	}, [component, ref.current]);

	return component;
}

export function useMollieErrors() {
	const [errors, setErrors] = useState<
		Record<MollieComponentType, string | null>
	>({
		card: null,
		cardHolder: null,
		cardNumber: null,
		expiryDate: null,
		verificationCode: null,
	});

	const handleError = useCallback(
		(type: MollieComponentType) => (message: string | null) =>
			setErrors((errors) => ({
				...errors,
				[type]: message,
			})),
		[],
	);

	return { ...errors, handleError };
}
